// useRouteCalculation.js
import { ref } from "vue";
import { useRouting } from "@/composables/routing/useRouting";

const { calculateRoutingInfo } = useRouting();

export function useRouteCalculation() {
  const routeGeom = ref(null);
  const routeSummary = ref(null);
  const isCalculating = ref(false);
  const error = ref(null);

  const calculateRoute = async ({
    waypoints,
    travelPoint = null,
    options = { units: "kilometers", costing: "auto" },
  }) => {
    error.value = null;
    isCalculating.value = true;

    try {
      if (!waypoints || waypoints.length < 2) {
        routeGeom.value = null;
        routeSummary.value = null;
        return {
          geometry: null,
          summary: null,
        };
      }

      // Format waypoints to required structure
      let formattedWaypoints = waypoints.map((waypoint) => ({
        lat: parseFloat(waypoint.lat),
        lon: parseFloat(waypoint.lng || waypoint.lon),
      }));

      // Add travel point as first and last location if provided
      if (travelPoint) {
        const formattedTravelPoint = {
          lat: parseFloat(travelPoint.lat),
          lon: parseFloat(travelPoint.lng || travelPoint.lon),
        };
        formattedWaypoints = [
          formattedTravelPoint,
          ...formattedWaypoints,
          formattedTravelPoint,
        ];
      }

      // Construct routing payload
      const routingPayload = {
        locations: formattedWaypoints,
        costing: options.costing || "auto",
        directions_options: {
          units: options.units || "kilometers",
        },
      };

      // Calculate the route
      const { trip, geoJsonGeometry } = await calculateRoutingInfo(
        routingPayload,
      );

      // Update refs
      routeGeom.value = geoJsonGeometry;
      routeSummary.value = trip.summary;

      return {
        geometry: geoJsonGeometry,
        summary: trip.summary,
      };
    } catch (err) {
      error.value = err;
      throw err;
    } finally {
      isCalculating.value = false;
    }
  };

  return {
    routeGeom,
    routeSummary,
    isCalculating,
    error,
    calculateRoute,
  };
}
